import { useMemo } from 'react'
import { useLocation } from '@fs/zion-router'
import { getKioskConfig, KIOSK_MENU_PATH } from '../../helpers/kioskConfig'
import getIsTWAMode from '../../helpers/twaMode'
import useActivityReturnTo from '../../hooks/useActivityReturnTo'
import useIsStationMode from '../../hooks/useIsStationMode'
import { useExperienceNavControlsRolloutEnabled } from './useExperienceNavControlsRollout'

const stripTrailingSlash = (path) => {
  if (path == null || path.length <= 1) return path

  let end = path.length
  while (end > 0 && path[end - 1] === '/') end -= 1

  return end === path.length ? path : path.slice(0, end)
}

/**
 * Which ways out of the experience apply here, from the runtime context alone
 * (mode, kiosk config, kids entry, route) rather than from what the experience
 * declared. Split out because deciding this is most of what the controls do,
 * and it reads better as one statement of the rules than as a dozen booleans
 * threaded through a render.
 *
 * @param {string|null} navHomePath the experience's own front door
 */
export default function useExperienceNavVisibility(navHomePath) {
  const location = useLocation()
  const isStation = useIsStationMode()
  const showPrototypeNavigation = useExperienceNavControlsRolloutEnabled()

  // Every STATION screen that mounts these controls offers Leave Kiosk: it is
  // the one way out of a docked kiosk, and the controls are its only chrome.
  // ConditionalHeader mounts them in STATION only under the rollout flag
  // (before it the legacy StationMenu is the chrome) and never on screens that
  // must show no kiosk chrome at all (the code-entry attract).
  const showLeaveKioskAction = isStation

  // Inside the Together app's WebView (TWA) the experience behaves like a
  // single-app kiosk: the level above it is the discovery-kids Activities tab,
  // reached through the returnTo param kids appends on in-WebView links. The
  // kids-return row renders wherever a return destination resolves - every
  // TWA page (useActivityReturnTo defaults to the Activities tab there when no
  // param arrived), and the kids PWA, which sends returnTo without the twa
  // signal - as an exit row below the experience's own rows, the way Leave
  // Kiosk is in STATION.
  const isTwa = useMemo(() => getIsTWAMode(), [])
  const kidsReturnTo = useActivityReturnTo()
  const showKidsReturn = !isStation && kidsReturnTo !== null

  // navMenu present means this kiosk offers multiple experiences.
  const isMultiApp = Boolean(getKioskConfig()?.navMenu)
  const currentPath = stripTrailingSlash(location.pathname)
  const atHome = navHomePath != null && currentPath === stripTrailingSlash(navHomePath)
  const atMenu = currentPath === KIOSK_MENU_PATH

  // Home points one level UP from wherever it appears: multi-app kiosks
  // always have the menu above them; single-app kiosks have NO level above the
  // experience at all (leaving it means undocking), so they show only Leave
  // Kiosk plus a registered Back — never Home; in the kids app context (TWA or
  // a returnTo entry) the level above is the kids Activities tab, owned by the
  // kids-return pill, so the activity-center Home never renders there; plain
  // WEB has the Discovery landing above the experience landing — there Home
  // shows ONLY at the landing (activities already have Back one hop from it)
  // and exits to the activity center. On the menu itself Home would be a
  // self-link — from there the only level up is undocking, which Leave Kiosk
  // owns.
  const showHome = isStation ? isMultiApp && !atMenu : showPrototypeNavigation && !isTwa && !showKidsReturn && atHome

  return { isStation, kidsReturnTo, showHome, showKidsReturn, showLeaveKioskAction }
}
